import { isEmpty, isString, omit } from 'lodash';
import moment, { isMoment } from 'moment';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import { InspectionType } from './type';
import {
  toFactoryFormValue,
  toSupplierFormValue
} from './components/Steps/ReviewSubmit/SubForms/SelectFactory/util';

const DEFAULT_INSPECTION = {
  inspectionPOItems: [],
  inspectionRequirement: {
    criticalLevel: null,
    criticalMajorLevel: null,
    criticalMinorLevel: null
  },
  additionalInformation: {},
  suppliers: [],
  factory: null
};

const parseInspectionFactory = (inspectionFactory) => {
  if (isEmpty(inspectionFactory)) return null;
  if (isEmpty(inspectionFactory.factory)) return null;
  const { contactPersonPhone, contactPersonEmail, contactPerson } =
    toFactoryFormValue(inspectionFactory.factory);

  return {
    ...inspectionFactory.factory,
    contactPerson: inspectionFactory.contactPerson || contactPerson,
    contactPersonEmail:
      inspectionFactory.contactPersonEmail || contactPersonEmail,
    contactPersonPhone: {
      code: inspectionFactory.areaCode || contactPersonPhone.code,
      phone: inspectionFactory.contactPersonPhone || contactPersonPhone.phone
    },
    name: get(inspectionFactory, 'factory.name', ''),
    inspectionFactoryId: inspectionFactory.id
  };
};

const parseAssignTo = (assignTo) => {
  if (Array.isArray(assignTo)) {
    return assignTo.map((item) => {
      const { inspectionDates = [] } = item;
      return {
        ...item,
        inspectionCalendars: keyBy(
          inspectionDates
            .filter((item) => item.date != null)
            .map((item) => {
              return {
                ...item,
                maxManday: (item.manday ?? 0) + (item.remainingDays ?? 1.5)
              };
            }),
          (item) => {
            return moment(item.date).format('YYYY-MM-DD');
          }
        )
      };
    });
  }
  return [];
};

export const parseInspection = (inspection) => {
  if (isEmpty(inspection)) return DEFAULT_INSPECTION;

  return {
    inspectionRequirement: {
      inspectionDate: moment(inspection.inspectionDate).format('DD/MM/YYYY'),
      criticalLevel: inspection.criticalLevel
        ? Number(inspection.criticalLevel)
        : null,
      criticalMajorLevel: inspection.criticalMajorLevel
        ? Number(inspection.criticalMajorLevel)
        : null,
      criticalMinorLevel: inspection.criticalMinorLevel
        ? Number(inspection.criticalMinorLevel)
        : null,
      typeOfService: inspection.typeOfService,
      generalInspectLevel: inspection.generalInspectLevel,
      inspectionEndDate: inspection.inspectionEndDate
        ? moment(inspection.inspectionEndDate).format('DD/MM/YYYY')
        : undefined,
      assignTo: parseAssignTo(inspection.assignTo),
      noOfManday: inspection.noOfManday
    },
    additionalInformation: {
      remark: inspection.remark,
      documentLists: inspection.documentList
    },
    inspectionPOItems: inspection.inspectionPOReportGroup
      ? inspection.inspectionPOReportGroup.inspectionPOItems.map((item) => {
          const { aql } = item;
          const acceptedAQL = {
            critical: get(aql, 'critical.acceptanceLevel') || 0,
            major: get(aql, 'major.acceptanceLevel') || 0,
            minor: get(aql, 'minor.acceptanceLevel') || 0
          };
          return {
            id: item.productProfileId,
            isSignSample: !!(item.isSignSample !== 0),
            poItemId: item.id, // NOTE: When transform will get poItem as Id
            productProfile: item.productProfile,
            name: item.productProfile.name,
            image: item.productProfile.image,
            itemNo: item.productProfile.itemNo,
            supplierName: get(item.productProfile, 'supplier.name', ''),
            factoryName: get(item.productProfile, 'factory.name', ''),
            inspectionSubPOItems: item.inspectionSubPOItems,
            inspectionAQL: {
              criticalLevel:
                item.criticalLevel || inspection.criticalLevel
                  ? Number(item.criticalLevel || inspection.criticalLevel)
                  : null,
              criticalMajorLevel:
                item.criticalMajorLevel || inspection.criticalMajorLevel
                  ? Number(
                      item.criticalMajorLevel || inspection.criticalMajorLevel
                    )
                  : null,

              criticalMinorLevel:
                item.criticalMinorLevel || inspection.criticalMinorLevel
                  ? Number(
                      item.criticalMinorLevel || inspection.criticalMinorLevel
                    )
                  : null
            },
            isCustomSampling: item.isCustomSampling,
            customSampleSizeCritical: item.customSampleSizeCritical,
            customSampleSizeMajor: item.customSampleSizeMajor,
            customSampleSizeMinor: item.customSampleSizeMinor,
            customSampleSizeCriticalAcceptance:
              item.customSampleSizeCriticalAcceptance,
            customSampleSizeMajorAcceptance:
              item.customSampleSizeMajorAcceptance,
            customSampleSizeMinorAcceptance:
              item.customSampleSizeMinorAcceptance,
            // defectAccepted: item.aql,
            defectAccepted: acceptedAQL,
            suppliers: item.productProfile.supplier
              ? [item.productProfile.supplier]
              : [],
            factories: item.productProfile.factory
              ? [item.productProfile.factory]
              : [],
            inspectionCriteria: item.inspectionCriteria,
            sampleSize: item.aql
          };
        })
      : [],
    factory: parseInspectionFactory(inspection.inspectionFactory),
    suppliers: inspection.inspectionSuppliers
      ? inspection.inspectionSuppliers.map((item) => {
          const { contactPersonPhone, contactPersonEmail, contactPerson } =
            toSupplierFormValue(item.supplier);
          return {
            ...item.supplier,
            inspectionSupplierId: item.id,
            contactPerson: item.contactPerson || contactPerson,
            contactPersonEmail: item.contactPersonEmail || contactPersonEmail,
            contactPersonPhone: {
              code: item.areaCode || contactPersonPhone.code,
              phone: item.contactPersonPhone || contactPersonPhone.phone
            }
          };
        })
      : []
  };
};

export const transformPayloadInspection = (
  formValue: Record<any, any>,
  rawData: InspectionType = {} as InspectionType
) => {
  const {
    inspectionPOItems,
    inspectionRequirement,
    additionalInformation,
    suppliers,
    factory
  } = formValue;

  const inspectionPOReportGroup = {
    // Added id POReport Group;
    inspectionPOItems: inspectionPOItems.map((item) => {
      const poItem = {
        isSignSample: item.isSignSample ? 1 : 0,
        productProfileId: item.productProfile
          ? item.productProfile.id
          : item.id,
        inspectionSubPOItems: item.inspectionSubPOItems,
        criticalLevel: item.inspectionAQL.criticalLevel,
        criticalMajorLevel: item.inspectionAQL.criticalMajorLevel,
        criticalMinorLevel: item.inspectionAQL.criticalMinorLevel,
        inspectionCriteria: item.inspectionCriteria,
        isCustomSampling: item.isCustomSampling,
        customSampleSizeCritical: item.customSampleSizeCritical,
        customSampleSizeMajor: item.customSampleSizeMajor,
        customSampleSizeMinor: item.customSampleSizeMinor,
        customSampleSizeCriticalAcceptance:
          item.customSampleSizeCriticalAcceptance,
        customSampleSizeMajorAcceptance: item.customSampleSizeMajorAcceptance,
        customSampleSizeMinorAcceptance: item.customSampleSizeMinorAcceptance
      };
      if (item.poItemId) {
        return {
          id: item.poItemId,
          ...poItem
        };
      }
      return poItem;
    })
  };
  if (rawData.inspectionPOReportGroup) {
    (inspectionPOReportGroup as any).id = rawData.inspectionPOReportGroup.id;
  }

  const inspectionFactory = factory
    ? {
        contactPerson: factory.contactPerson,
        contactPersonEmail: factory.contactPersonEmail,
        areaCode: factory.contactPersonPhone.code
          ? `${factory.contactPersonPhone.code}`
          : null,
        contactPersonPhone: factory.contactPersonPhone.phone
          ? `${factory.contactPersonPhone.phone}`
          : null,
        factoryId: factory.id
      }
    : null;
  if (rawData.inspectionFactory && inspectionFactory) {
    (inspectionFactory as any).id = rawData.inspectionFactory.id;
  }
  return {
    inspectionRequirement: {
      ...omit(inspectionRequirement, 'assignTo'),
      inspectionDate: inspectionRequirement.inspectionDate
        ? moment(inspectionRequirement.inspectionDate, 'DD/MM/YYYY').format(
            'YYYY-MM-DD'
          )
        : null,
      inspectionEndDate: inspectionRequirement.inspectionEndDate
        ? moment(inspectionRequirement.inspectionEndDate, 'DD/MM/YYYY').format(
            'YYYY-MM-DD'
          )
        : null
    },
    inspectionPOReportGroup,
    addionalInformation: {
      ...omit(additionalInformation, 'documentLists'),
      documentListIds: additionalInformation.documentLists?.map(({ id }) => id)
    },
    inspectionFactory,
    assignTo: inspectionRequirement.assignTo.map((assigned: any) => {
      return {
        inspectionDates: assigned.inspectionDates.map((inspectionDate: any) => {
          const toMoment = (date: string) => {
            if (isMoment(date)) return date;
            return moment(date);
          };
          return {
            date: toMoment(inspectionDate.date).format('YYYY-MM-DD'),
            manday: isString(inspectionDate.manday)
              ? Number(inspectionDate.manday)
              : inspectionDate.manday
          };
        }),

        userId: assigned.userId
      };
    }),
    inspectionSuppliers: suppliers.map((item) => {
      const supplier = {
        contactPerson: item.contactPerson,
        contactPersonEmail: item.contactPersonEmail,
        areaCode: item.contactPersonPhone.code
          ? `${item.contactPersonPhone.code}`
          : null,
        contactPersonPhone: item.contactPersonPhone.phone
          ? `${item.contactPersonPhone.phone}`
          : null,
        supplierId: item.id
      };
      if (item.inspectionSupplierId) {
        (supplier as any).id = item.inspectionSupplierId;
      }
      return supplier;
    })
  };
};

export const filterOutItems = (value, selectedItems) => {
  return value.filter(({ id }) => {
    const existed = selectedItems.find((selected) => {
      return selected.id === id;
    });
    return !!existed;
  });
};

export const filterOutNewlySelectedItems = (existedValue, selectedItems) => {
  return selectedItems.filter((selected) => {
    return !existedValue.find((existed) => {
      return existed.id === selected.id;
    });
  });
};

export const extractChangedValues = (current, previous) => {
  return Object.keys(current).reduce((acc, key) => {
    if (current[key] !== previous[key]) {
      acc[key] = current[key];
    }
    return acc;
  }, {});
};
